import {
  Badge,
  Blockquote,
  Grid,
  Group,
  LoadingOverlay,
  Stack,
  Text,
  Tooltip,
  useMantineTheme,
} from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconAsterisk, IconInfoCircle } from '@tabler/icons-react';
import { useDeclinePaymentPlanRequest, useGetActivities, useUpdateTasksStatus } from 'api';
import { TaskActionType, type UpdateTasksStatusBody } from 'api/models';
import { Comments } from 'app/Private/Comments';
import InvoiceTimeline from 'app/Private/Invoices/Invoice/InvoiceTimeline/InvoiceTimeline';
import { ClientHoverCard } from 'components/ClientHoverCard';
import { InfoRow } from 'components/InfoRow';
import { PageTitle } from 'components/PageTitle';
import { Panel } from 'components/Panel';
import AttachedInvoicesList from 'components/shared/AttachedInvoices';
import { i18nKeys, tKeys, useTranslation } from 'locales';
import { useLoadTaskTypes } from 'modules/Tasks/api/use-load-task-types';
import { TaskForm } from 'modules/Tasks/TaskForm';
import { useHistory, useParams } from 'react-router';
import { isNonNullish } from 'remeda';
import HTML from 'shared/components/HTML/HTML';
import { useCreateTaskComment } from 'shared/hooks/use-create-task-comment';
import { useLoadComments } from 'shared/hooks/use-load-comments';
import { useLoadTask } from 'shared/hooks/use-load-task';
import { Button, ButtonColor } from 'shared/io';
import { formatActivities } from 'shared/utils/activities.utils';
import { DialogShowId, dialogHide } from 'store/view/view.actions';
import { getTaskTypeDescription } from './Task.utils';
import { TaskActions } from './TaskActions/TaskActions';
import { TaskAssignees } from './TaskAssignees';
import { TasksListItemStatus } from './TasksList/TasksListItemStatus';

export const TaskDetail = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const theme = useMantineTheme();

  const { taskTypes } = useLoadTaskTypes();

  const { id: taskId } = useParams<{ id: string }>();

  const { task, isTaskLoading, taskError, refetchTask } = useLoadTask(taskId);

  const { mutate: updateTasksStatus } = useUpdateTasksStatus();

  const { createTaskComment, isCreateTaskCommentLoading } = useCreateTaskComment();
  const { mutate: declinePaymentPlanRequest } = useDeclinePaymentPlanRequest();

  const { comments } = useLoadComments({ resourceType: 'task', resourceId: taskId });

  const {
    data: activitiesListResponse,
    refetch: refetchActivities,
    isLoading: isActivitiesLoading,
    isError: isActivitiesError,
  } = useGetActivities({
    task_id: taskId,
  });

  if (isTaskLoading && !task && isActivitiesLoading) {
    return <LoadingOverlay visible />;
  }

  if (taskError || !task || isActivitiesError || !activitiesListResponse) {
    return <span>Error</span>;
  }

  const goToList = () => {
    history.goBack();
  };

  const {
    id,
    title,
    mandatory: isMandatory,
    createdAt,
    statusKey,
    status,
    debtor,
    description,
    invoices,
    dueDate,
    daysPostponed,
    aiCallAutogenerated: isAiCallAutogenerated,
    debtorReaction,
    completed: isCompleted,
    skipped: isSkipped,
    taskType: { uid },
  } = task;

  const dueDateAdjusted = dueDate ? dueDate.add(daysPostponed, 'day') : null;

  const handleUpdateTask = () => {
    modals.open({
      title: t(i18nKeys.FORM.TASK.EDIT),
      size: 'xl',
      children: <TaskForm refetchTasks={refetchTask} taskId={id.toString()} />,
    });
    refetchActivities();
  };

  const handleUpdateStatus = (data: UpdateTasksStatusBody) =>
    updateTasksStatus(
      {
        data,
      },
      {
        onSuccess: () => {
          if (data.action_type === TaskActionType.delete) {
            goToList();
          } else {
            refetchTask();
            refetchActivities();
          }
          dialogHide(DialogShowId.CUSTOM);
        },
      },
    );

  const activitiesListFormated = formatActivities(activitiesListResponse);

  const contactInfo = () => {
    if (!debtor) return null;

    switch (task.taskTypeId) {
      case 1:
        return (
          <InfoRow label={t(tKeys.PHONES)}>
            {debtor.phones.map(({ number }) => (
              <a key={number} href={`tel:${number}`}>
                {number}
              </a>
            ))}
          </InfoRow>
        );
      case 2:
        return (
          <InfoRow label={t(tKeys.EMAIL)}>
            {debtor.emails.map(email => (
              <a key={email} href={`mailto:${email}`}>
                <span>{email}</span>
              </a>
            ))}
          </InfoRow>
        );
      default:
        return null;
    }
  };

  const goToPaymentPlanCreation = () => {
    const invoiceIds = invoices?.details.map(({ id: invoiceId }) => invoiceId) ?? [];
    history.push(`/payment-plans/new?debtor_id=${debtor?.id}&invoice_ids=${invoiceIds}`);
  };

  const refusePlan = () =>
    declinePaymentPlanRequest(
      {
        data: { task_id: taskId },
      },
      {
        onSuccess: () => {
          refetchTask();
          refetchActivities();
          dialogHide(DialogShowId.CUSTOM);
        },
      },
    );

  const isSkippedOrCompleted = isSkipped || isCompleted;

  return (
    <>
      <PageTitle>
        {title}
        <PageTitle.Back />
        <PageTitle.Actions>
          {uid === 'payment_plan' && (
            <>
              <Button
                noMargin
                label={t(tKeys.TASK.PAYMENT_PLAN.ACCEPT)}
                color={ButtonColor.GREEN}
                onClick={goToPaymentPlanCreation}
                disabled={isSkippedOrCompleted}
                noShadow
              />
              <Button
                noMargin
                label={t(tKeys.TASK.PAYMENT_PLAN.DECLINE)}
                color={ButtonColor.RED}
                onClick={refusePlan}
                disabled={isSkippedOrCompleted}
                noShadow
              />
            </>
          )}
          <TaskActions taskIds={[id]} onClick={handleUpdateStatus} onClickEdit={handleUpdateTask} />
        </PageTitle.Actions>
      </PageTitle>

      <Grid>
        <Grid.Col span={6}>
          <Panel
            title={t(tKeys.CLIENT.GENERAL_INFO)}
            style={{ height: '100%' }}
            withBorder
            topRight={
              isMandatory && (
                <Badge
                  size="md"
                  variant="light"
                  leftSection={<IconAsterisk size={14} />}
                  color="orange"
                >
                  {t(tKeys.MANDATORY)}
                </Badge>
              )
            }
          >
            <Stack gap={12}>
              <InfoRow label={t(tKeys.TASK.ATTRIBUTES.STATUS)}>
                <TasksListItemStatus statusKey={statusKey} status={status} />
              </InfoRow>
              <InfoRow label={t(tKeys.CREATION_DATE)}>{createdAt.format('LL')}</InfoRow>
              <InfoRow label={t(tKeys.DUE_DATE)}>
                {dueDateAdjusted ? (
                  <Group gap={4} align="center">
                    <InfoRow.Value>{dueDateAdjusted.format('LL')}</InfoRow.Value>
                    {isNonNullish(daysPostponed) && daysPostponed > 0 && dueDate && (
                      <Tooltip
                        label={`${dueDate.format('LL')} + ${t(tKeys.DAY_S, { count: daysPostponed })}`}
                      >
                        <IconInfoCircle size={16} color={theme.colors.warning[5]} />
                      </Tooltip>
                    )}
                  </Group>
                ) : (
                  '-'
                )}
              </InfoRow>
              <InfoRow label={t(tKeys.TASK.ATTRIBUTES.TASK_TYPE)}>
                {getTaskTypeDescription(taskTypes.data, task.taskTypeId)}
              </InfoRow>
              <InfoRow label={t(tKeys.TASK.ATTRIBUTES.ASSIGNEES)}>
                <TaskAssignees primary={task.assignee} additional={task.additionalAssignees} />
              </InfoRow>
              {isNonNullish(debtor) && (
                <>
                  <InfoRow label={t(tKeys.TASK.ATTRIBUTES.DEBTOR)}>
                    <ClientHoverCard debtorId={debtor.id} debtorName={String(debtor.fullName)} />
                  </InfoRow>
                  {contactInfo()}
                </>
              )}
            </Stack>
          </Panel>
        </Grid.Col>
        <Grid.Col span={6}>
          <Panel title={t(tKeys.DESCRIPTION)} withBorder style={{ height: '100%' }}>
            {isNonNullish(debtorReaction) && (
              <Blockquote
                mb="md"
                p="sm"
                cite={[
                  t(tKeys.TIMELINE.DEBTOR_REACTION),
                  debtorReaction.authorName,
                  debtorReaction.invoiceReference,
                  debtorReaction.createdAt.format('LL'),
                ]
                  .filter(isNonNullish)
                  .join(' · ')}
              >
                <Text style={{ whiteSpace: 'pre-wrap' }}>{debtorReaction.comment}</Text>
              </Blockquote>
            )}
            {isAiCallAutogenerated ? <HTML html={description} /> : <Text>{description}</Text>}
          </Panel>
        </Grid.Col>
        {!!invoices?.details.length && (
          <Grid.Col span={12}>
            <AttachedInvoicesList invoices={invoices} />
          </Grid.Col>
        )}
        {!!activitiesListFormated.activities.length && (
          <Grid.Col span={12}>
            <InvoiceTimeline
              metadata={activitiesListFormated.metadata}
              activities={activitiesListFormated.activities}
            />
          </Grid.Col>
        )}
        <Grid.Col span={12}>
          <Comments
            comments={comments}
            submitComment={createTaskComment}
            resourceId={taskId}
            isCreateCommentPending={isCreateTaskCommentLoading}
          />
        </Grid.Col>
      </Grid>
    </>
  );
};
